// Grid 再描画まわりの待機制御をまとめる coordinator です。
// pending ref の操作と再試行の順序だけをここへ寄せ、画面固有処理は deps から受け取ります。

export function createGridRefreshCoordinator(deps) {
    // requestAnimationFrame が使える場合は描画タイミングへ寄せます。
    function runDeferred(callback) {
        if (typeof deps.requestAnimationFrame === "function") {
            deps.requestAnimationFrame(callback);
            return;
        }
        deps.setTimeout(callback, 0);
    }

    // requestAnimationFrame / setTimeout のどちらでも解除できるようにします。
    function clearScheduledHandle(timerId) {
        if (deps.isEmpty(timerId)) return;
        if (typeof deps.cancelAnimationFrame === "function") {
            deps.cancelAnimationFrame(timerId);
        }
        deps.clearTimeout(timerId);
    }

    // 原価 Grid の editable 状態を今すぐ更新できるか判定します。
    function canRefreshGenkaEditableState(panelInfo) {
        if (deps.isEmpty(panelInfo)) return false;
        const api = deps.getGridApi(panelInfo);
        if (deps.isEmpty(api)) return false;
        return deps.isGridDomReady(panelInfo);
    }

    // pending な原価 editable 更新を、Grid 準備完了後に流します。
    function flushPendingGenkaEditableRefresh(source = "") {
        const current = deps.getPendingGenkaEditableRefresh();
        if (current.requested !== true) return false;

        const genkaPanel = deps.getGenkaPanel();
        if (!canRefreshGenkaEditableState(genkaPanel)) return false;

        deps.resetPendingGenkaEditableRefresh();
        return deps.refreshGenkaGridEditableState(genkaPanel, {
            source: source !== "" ? source : "flushPendingGenkaEditableRefresh"
        });
    }

    // 原価 editable 更新がまだ無理な場合は、短いリトライを積みます。
    function schedulePendingGenkaEditableRefresh(reason = "") {
        deps.setPendingGenkaEditableRefresh({
            requested: true,
            reason: String(reason ?? ""),
            retries: Number(deps.DicVal(deps.getPendingGenkaEditableRefresh(), "retries", 0))
        });

        if (!deps.isEmpty(deps.getPendingGenkaEditableRefreshTimer())) return;

        const retry = () => {
            deps.setPendingGenkaEditableRefreshTimer(null);

            const current = deps.getPendingGenkaEditableRefresh();
            if (current.requested !== true) return;
            if (flushPendingGenkaEditableRefresh("pendingRetry")) return;

            const retries = Number(deps.DicVal(current, "retries", 0)) + 1;
            deps.setPendingGenkaEditableRefresh({
                ...current,
                retries
            });

            if (retries >= 10) return;
            deps.setPendingGenkaEditableRefreshTimer(deps.setTimeout(retry, 50));
        };

        deps.setPendingGenkaEditableRefreshTimer(deps.setTimeout(retry, 0));
    }

    // post-load の visible refresh で対象を残すべき Grid だけを判定します。
    function shouldKeepPostLoadVisibleGridRefresh(panelInfo) {
        if (deps.isEmpty(panelInfo)) return true;
        if (deps.isDemandPanelInfo(panelInfo)) return true;
        if (deps.isGenkaGridPanelInfo(panelInfo)) return deps.getGenkaPostLoadRefreshSkip() !== true;
        if (deps.isWashPanelInfo(panelInfo)) return true;

        const rows = deps.getGridRows(panelInfo);
        return Array.isArray(rows) && rows.length !== 0;
    }

    // 既存 rerenderGrid 経路で、保存済み rowData の再読込を予約します。
    function scheduleGridDataRerender(panelInfo) {
        try {
            const id = deps.getGridDomId(panelInfo);
            if (deps.isEmpty(id)) return;

            deps.setPendingGridRerenderIds({
                ...deps.getPendingGridRerenderIds(),
                [id]: true
            });

            if (!deps.isEmpty(deps.getPendingGridRerenderTimer())) return;

            const flush = () => {
                deps.setPendingGridRerenderTimer(null);

                const ids = Object.keys(deps.getPendingGridRerenderIds());
                deps.setPendingGridRerenderIds({});

                ids.forEach((gridId) => {
                    deps.rerenderGridById(gridId);
                });
            };

            if (typeof deps.requestAnimationFrame === "function") {
                deps.setPendingGridRerenderTimer(deps.requestAnimationFrame(flush));
            } else {
                deps.setPendingGridRerenderTimer(deps.setTimeout(flush, 0));
            }
        } catch (e) {
            console.error(e);
        }
    }

    // API 準備後に再反映が必要な panel を記録します。
    function markPendingGridApply(panelInfo) {
        try {
            const key = deps.getGridDomId(panelInfo);
            if (deps.isEmpty(key)) return;

            deps.setPendingGridApplyMap({
                ...deps.getPendingGridApplyMap(),
                [key]: true
            });
        } catch (e) {
        }
    }

    // 反映待ち記録を解除します。
    function clearPendingGridApply(panelInfo) {
        try {
            const key = deps.getGridDomId(panelInfo);
            if (deps.isEmpty(key)) return;

            const currentMap = { ...deps.getPendingGridApplyMap() };
            delete currentMap[key];
            deps.setPendingGridApplyMap(currentMap);
        } catch (e) {
        }
    }

    // 保存済み row を API へ再反映し、待機記録も消します。
    function flushPendingGridApply(panelInfo) {
        try {
            const key = deps.getGridDomId(panelInfo);
            if (deps.isEmpty(key)) return false;

            const currentMap = deps.getPendingGridApplyMap();
            if (!currentMap[key]) return false;

            const rows = deps.getGridRows(panelInfo);
            clearPendingGridApply(panelInfo);
            deps.applyGridRowsNow(panelInfo, rows);
            return true;
        } catch (e) {
            return false;
        }
    }

    // clearform 側などからタイマー掃除に使えるようにまとめます。
    function clearAllPendingTimers() {
        clearScheduledHandle(deps.getPendingGridRerenderTimer());
        deps.setPendingGridRerenderTimer(null);

        clearScheduledHandle(deps.getPendingGenkaEditableRefreshTimer());
        deps.setPendingGenkaEditableRefreshTimer(null);
    }

    // defer 実行に使う helper を露出し、将来の拡張点にします。
    function scheduleDeferred(callback) {
        runDeferred(callback);
    }

    return {
        canRefreshGenkaEditableState,
        flushPendingGenkaEditableRefresh,
        schedulePendingGenkaEditableRefresh,
        shouldKeepPostLoadVisibleGridRefresh,
        scheduleGridDataRerender,
        markPendingGridApply,
        clearPendingGridApply,
        flushPendingGridApply,
        clearAllPendingTimers,
        scheduleDeferred
    };
}
